import {
  ApiFormFieldJson,
  ApiFormLevelJson,
  FormLevel,
  convertApiFieldsToFormFields,
  convertApiLevelsToFormLevels,
  convertFieldToApiFormat,
  convertLevelToApiFormat,
  extractUnknownProps,
  generateUUID,
  parseTemplateFieldId,
} from './form-builder.utils';

/**
 * Übersetzt zwischen gespeicherter Vorlage und der Struktur, mit der der
 * Designer arbeitet (DOCU-1066).
 *
 * Diese Klammer lag bis dahin in der Editor-Seite des Backoffice. Sie liegt
 * hier, weil das AdminPanel dieselben Vorlagen bearbeitet und ein Nachbau die
 * Durchschleif-Regeln unten früher oder später übersehen würde — mit dem
 * Ergebnis, dass ein Speichern aus der einen Oberfläche Einstellungen
 * verwirft, welche die andere gesetzt hat.
 */

/** Minimalausschnitt einer Vorlage, wie beide generierten API-Clients sie liefern. */
export interface TemplateLike {
  name?: string | null;
  description?: string | null;
  auditFormJson?: any;
  entryFormJson?: any;
}

/**
 * DOCU-664: Keys, die der Editor auf Form-Root-Ebene selbst erzeugt. Alles
 * andere wird aus dem Original durchgeschleift.
 */
const MANAGED_AUDIT_FORM_KEYS = new Set([
  'id', 'version', 'name', 'fields', 'uiDescriptor', 'children', 'exportHideEmptyRows',
]);

/**
 * DOCU-684: Keys, die der Editor innerhalb des uiDescriptor neu baut. Der
 * verschachtelte Rest (z. B. listItem.leadingImage) wird durchgeschleift.
 */
const MANAGED_UIDESCRIPTOR_KEYS = new Set(['formTitle', 'description', 'listItem', 'confirmDialog']);
const MANAGED_LISTITEM_KEYS = new Set(['titleTemplate', 'subTitleTemplate']);
const MANAGED_CONFIRMDIALOG_KEYS = new Set(['titleTemplate']);

export const DEFAULT_ROOT_LEVEL_NAME = 'Dokumentation';

/** Ein leeres Root-Level, wie es ein frischer Editor zeigt. */
export function createEmptyRootLevel(): FormLevel {
  return {
    id: 'root',
    name: DEFAULT_ROOT_LEVEL_NAME,
    description: '',
    fields: [],
    children: [],
    isExpanded: true,
  };
}

/**
 * Baut aus einer gespeicherten Vorlage die Editor-Struktur.
 *
 * Kennt weiterhin den Altfall `entryFormJson` ohne `children`: solche Vorlagen
 * werden beim Öffnen nach `children` migriert (ADR 0001 — auditFormJson ist die
 * alleinige Quelle).
 */
export function templateToRootLevel(template: TemplateLike): FormLevel {
  const rootLevel = createEmptyRootLevel();
  const auditJson: any = template.auditFormJson;

  if (auditJson?.fields) {
    rootLevel.fields = convertApiFieldsToFormFields(auditJson.fields as ApiFormFieldJson[]);
    // Neues Format hält den Root-Namen in `name`; das alte in `formTitle`.
    rootLevel.name = auditJson.name || auditJson.uiDescriptor?.formTitle || DEFAULT_ROOT_LEVEL_NAME;
    rootLevel.titleFieldId = parseTemplateFieldId(auditJson.uiDescriptor?.listItem?.titleTemplate);
    rootLevel.subtitleFieldId = parseTemplateFieldId(auditJson.uiDescriptor?.listItem?.subTitleTemplate);
    rootLevel.extra = extractUnknownProps(auditJson, MANAGED_AUDIT_FORM_KEYS);
  }

  const auditChildren = auditJson?.children as ApiFormLevelJson[] | undefined;
  if (auditChildren && auditChildren.length > 0) {
    rootLevel.children = convertApiLevelsToFormLevels(auditChildren);
  } else if (template.entryFormJson?.fields) {
    const entryFormJson: any = template.entryFormJson;
    rootLevel.children = [
      {
        id: 'entry_' + Date.now(),
        name: entryFormJson.uiDescriptor?.formTitle || 'Eintrag',
        description: '',
        titleFieldId: parseTemplateFieldId(entryFormJson.uiDescriptor?.listItem?.titleTemplate),
        subtitleFieldId: parseTemplateFieldId(entryFormJson.uiDescriptor?.listItem?.subTitleTemplate),
        fields: convertApiFieldsToFormFields(entryFormJson.fields as ApiFormFieldJson[]),
        children: convertApiLevelsToFormLevels(entryFormJson.children),
        isExpanded: true,
      },
    ];
  }

  return rootLevel;
}

export interface BuildAuditFormJsonOptions {
  rootLevel: FormLevel;
  /** Vorlagenname — landet als `uiDescriptor.formTitle`. */
  formTitle: string;
  description?: string;
  /** DOCU-818: formularweit leere Zeilen im Export ausblenden. */
  exportHideEmptyRows?: boolean;
  /** Die geladene Vorlage, aus der unbekannte Properties übernommen werden. */
  original?: TemplateLike | null;
}

/**
 * Baut das `auditFormJson` für das Speichern.
 *
 * `entryFormJson` schreibt der Designer nicht mehr (ADR 0001); der Aufrufer
 * schickt dafür `null`, was die Spalte beim nächsten Speichern leert.
 */
export function buildAuditFormJson(options: BuildAuditFormJsonOptions): any {
  const { rootLevel, formTitle, description, exportHideEmptyRows, original } = options;

  // auditFormJson.id ist NICHT die Template-Id, sondern die Form-Id darin.
  const auditFormId = original?.auditFormJson?.id || generateUUID();
  const children = rootLevel.children.map((child) => convertLevelToApiFormat(child));

  const titleTemplate = rootLevel.titleFieldId
    ? `#{${rootLevel.titleFieldId}}`
    : (formTitle || rootLevel.name || DEFAULT_ROOT_LEVEL_NAME);
  const subTitleTemplate = rootLevel.subtitleFieldId
    ? `#{${rootLevel.subtitleFieldId}}`
    : '#{date}';

  const origUiDescriptor: any = original?.auditFormJson?.uiDescriptor ?? {};
  const uiDescriptorExtra = extractUnknownProps(origUiDescriptor, MANAGED_UIDESCRIPTOR_KEYS);
  const listItemExtra = extractUnknownProps(origUiDescriptor.listItem, MANAGED_LISTITEM_KEYS);
  const confirmDialogExtra = extractUnknownProps(origUiDescriptor.confirmDialog, MANAGED_CONFIRMDIALOG_KEYS);

  return {
    ...(rootLevel.extra ?? {}),
    id: auditFormId,
    version: 1,
    // Der Root-Anzeigename steht getrennt vom Vorlagennamen (formTitle).
    name: rootLevel.name || DEFAULT_ROOT_LEVEL_NAME,
    // DOCU-818: nur schreiben, wenn gesetzt (Default = aus).
    ...(exportHideEmptyRows ? { exportHideEmptyRows: true } : {}),
    fields: rootLevel.fields.map((f, index) => convertFieldToApiFormat(f, index)),
    uiDescriptor: {
      ...(uiDescriptorExtra ?? {}),
      formTitle,
      description: description || '',
      // #{...}, nicht {{...}} — so liest es die Flutter-App.
      listItem: {
        ...(listItemExtra ?? {}),
        titleTemplate,
        subTitleTemplate,
      },
      confirmDialog: {
        ...(confirmDialogExtra ?? {}),
        titleTemplate,
      },
    },
    children: children.length > 0 ? children : undefined,
  };
}
